import { currencyDigits } from './currencies';

/**
 * Formatters for the tenant-ops pages. Null renders as an em dash everywhere — "not collected" and
 * "collected and zero" are different facts (CP3), and a formatter is where that is most easily lost.
 */

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** 1024-based, one decimal from KB up: `1073741824` → `1.0 GB`. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes === 0) return '0 B';
  const negative = bytes < 0;
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const text = unit === 0 ? String(Math.round(value)) : value.toFixed(1);
  return `${negative ? '-' : ''}${text} ${BYTE_UNITS[unit]}`;
}

/**
 * A clean axis ceiling for BYTES: 1/2/5 × 10ⁿ in the largest 1024-based unit the max reaches, so the
 * quarter ticks land on 512 MB / 1 GB / 1.5 GB instead of 476.8 MB / 953.7 MB.
 */
export function niceBytesCeiling(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1024;
  let unit = 1;
  while (max / unit >= 1024 && unit < 1024 ** 5) unit *= 1024;
  const scaled = max / unit;
  const magnitude = 10 ** Math.floor(Math.log10(scaled));
  const normalised = scaled / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude * unit;
}

export function formatNumber(value: number | null | undefined, maxFractionDigits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', { maximumFractionDigits: maxFractionDigits });
}

/** A 0–1 ratio as a percent. */
export function formatRatio(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** An already-percent value (`35.5` → `35.5%`). */
export function formatPercentValue(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

/** Basis points: `1400` → `14%`, `2050` → `20.5%`. */
export function formatBps(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return '—';
  return `${(bps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

/**
 * Minor-unit digits of an ISO currency (EGP/USD 2, JPY 0, KWD and IQD 3) — BILLING's table (`GET …/billing/currencies`,
 * seeded with billing's own list), never `Intl`: the browser says IQD has 0 digits, billing stores it with 3, and one
 * disagreement is a 1000× wrong amount (money review C10). Unknown codes take billing's default, 2.
 */
export function minorDigits(currency: string | null | undefined): number {
  return currencyDigits(currency);
}

/** An estimated CPU figure in cores; a tiny non-zero share reads "< 0.01 cores", never "0.00". */
export function formatCores(cores: number | null | undefined): string {
  if (cores === null || cores === undefined || !Number.isFinite(cores)) return '—';
  if (cores > 0 && cores < 0.01) return '< 0.01 cores';
  return `${cores.toFixed(2)} cores`;
}

/**
 * Integer minor units as money, in full — never abbreviated (a "$1.2K" invoice is not actionable).
 * The currency's own minor-unit digits are used, so a 3-decimal currency is not off by ten.
 */
export function formatMinor(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—';
  const digits = minorDigits(currency);
  const major = minor / 10 ** digits;
  if (!currency) return major.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(major);
  } catch {
    return `${major.toLocaleString('en-US')} ${currency}`;
  }
}

/**
 * Parses a major-unit amount typed by an admin (`"199.50"`) into integer minor units for `currency`.
 * String arithmetic, so `0.29` is 29 piasters and not 28.999…; more decimals than the currency has is
 * refused rather than rounded.
 */
export function parseMajorToMinor(
  input: string,
  currency: string,
): { ok: true; minor: number } | { ok: false; error: string } {
  const text = input.trim().replace(/,/g, '');
  if (text === '') return { ok: false, error: 'Enter an amount.' };
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return { ok: false, error: 'Enter a non-negative amount.' };

  const digits = minorDigits(currency);
  const fraction = match[2] ?? '';
  if (fraction.length > digits) {
    return { ok: false, error: `${currency} has ${digits} decimal place(s).` };
  }

  const minor = Number(match[1]) * 10 ** digits + Number(fraction.padEnd(digits, '0') || '0');
  if (!Number.isSafeInteger(minor)) return { ok: false, error: 'That amount is too large.' };
  return { ok: true, minor };
}

/** Minor units back to the major-unit text an input field shows. */
export function minorToMajorText(minor: number, currency: string): string {
  const digits = minorDigits(currency);
  return (minor / 10 ** digits).toFixed(digits);
}

/** An absolute timestamp with the year — ops screens are read weeks later. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A plain day (`2026-09-26`) → `26 Sep`. */
export function formatDay(day: string): string {
  const date = new Date(`${day.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** An ISO instant → a short axis label (`09:05` inside a day, `26 Sep 09:05` otherwise). */
export function formatAxisTime(iso: string, withDay: boolean): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (!withDay) return time;
  return `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
}

/** Milliseconds for a latency cell: `240 ms`, `1.4 s`. */
export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** A `datetime-local` input value (the admin's local time) → an ISO instant with `Z`. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** The current month as `YYYY-MM` (UTC — billing periods are UTC months). */
export function currentPeriod(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The last `count` periods, newest first. */
export function recentPeriods(count: number, now: Date = new Date()): string[] {
  const out: string[] = [];
  let year = now.getUTCFullYear();
  let month = now.getUTCMonth();
  for (let i = 0; i < count; i++) {
    out.push(`${year}-${String(month + 1).padStart(2, '0')}`);
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }
  return out;
}

/** `2026-09` → `Sep 2026`. */
export function formatPeriod(period: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Short id for a table cell — the full id stays in the title. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}
